import Console from 'utility/Debug/Console';

export type Observer<T> = (state: T) => void;
export type Unsubscribe = () => void;
export type Subscribe<T> = (observer: Observer<T>) => Unsubscribe;
export type GetState<T> = () => T;
export type SetState<T> = (newState: T) => void;
export interface StateContainer<T> {
    getState: GetState<T>;
    setState: SetState<T>;
    subscribe: Subscribe<T>;
    synchronize: Subscribe<T>;
}

enum SyncPhase {
    Idle,
    Sync,
    Notify,
}

/**
 * @template T
 * @param {T} initialState
 */
export function createStateContainer<T>(initialState: T): StateContainer<T> {
    // our state
    let state = initialState;
    // a list of callback-functions that should respond
    // to state changes
    const observers: Set<Observer<T>> = new Set();
    const synchronizations: Set<Observer<T>> = new Set();
    let syncDepth = 0;
    let syncPhase = SyncPhase.Idle;

    const forEach = (
        iterable: Iterable<Observer<T>>,
        callback: (observer: Observer<T>) => void
    ) => {
        // we need to make a copy of our observers list
        // to prevent any new observer from leaking into
        // our notify/sync process
        Array.from(iterable).forEach(callback);
    };

    /**
     * tell all registered observers about the current state
     */
    const notifySync = () => {
        forEach(synchronizations, (observer) => observer(state));
    };

    const notifyFinalState = () => {
        // console.log('notify');
        // we don't need to make a copy of our observers list
        // to prevent any new observer from leaking into
        // our notify process
        // because our observers set is immutable
        forEach(observers, (observer) => observer(state));
    };

    /**
     * return the current state
     */
    const getState: GetState<T> = () => {
        return state;
    };

    const startSyncQueue = () => {
        syncPhase = SyncPhase.Sync;
        syncDepth++;
        // console.log('startSyncQueue', syncDepth);
    };

    const endSyncQueue = () => {
        syncDepth--;
        // console.log('endSyncQueue', syncDepth);
        if (syncDepth === 0) {
            // if all synchronizations are done, notify observers
            syncPhase = SyncPhase.Notify;
            notifyFinalState();
            syncPhase = SyncPhase.Idle;
        }
    };

    /**
     * set the state, run synchronizations and notify all observers
     */
    const setState: SetState<T> = (newState) => {
        if (syncPhase === SyncPhase.Notify) {
            const warn =
                process.env.NODE_ENV === 'production'
                    ? Console.warn
                    : console.warn; // eslint-disable-line
            warn('You should not change the state during the notify phase.');
        }
        startSyncQueue();
        state = newState;
        notifySync();
        endSyncQueue();
    };

    /**
     * register a new observer that should be notified
     * on state changes. you may call setState within the observer
     */
    const synchronize: Subscribe<T> = (observer) => {
        synchronizations.add(observer);
        return function unsynchronize() {
            synchronizations.delete(observer);
        };
    };

    /**
     * register a new observer that should be notified
     * on state changes. you should not call setState within the observer
     */
    const subscribe: Subscribe<T> = (observer) => {
        observers.add(observer);
        return function unsubscribe() {
            observers.delete(observer);
        };
    };

    return {
        getState,
        setState,
        subscribe,
        synchronize,
    };
}
